import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../layout.js";
import Breadcrumbs from "../../component/Breadcrumbs.js";
import ButtonPrimary from "../../component/ButtonPrimary.js";
import InsightsCard from "../../component/InsightsCard.js";
import { apiCall, manageNetworkError } from "../../assets/js/common.js";
import NewsletterForm from "../../component/NewsletterForm.js";
import * as Constants from "../../config/constants.js";

export default function Resources({ pageName }) {
  const { type } = useParams();
  const navigate = useNavigate();

  const resourceType = useMemo(() => {
    return type ? type.toString().trim().toLowerCase() : "all";
  }, [type]);

  const isNewsletterView = resourceType === "newsletter";

  const canonicalUrl = useMemo(() => {
    if (resourceType === "all") {
      return `${Constants.SITE_URL}/resources/`;
    }
    return `${Constants.SITE_URL}/resources/${resourceType}/`;
  }, [resourceType]);

  const [filterData, setFilterData] = useState({});
  const [recordDetails, setRecordDetails] = useState([]);
  const [contentTypeDetails, setContentTypeDetails] = useState([]);
  const [contentTopicDetails, setContentTopicDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const state = JSON.parse(localStorage.getItem(Constants.RESOURCES_KEY_NAME));

  const getRecordDetails = async () => {
    setLoading(true);
    const endpoint = `resources/${resourceType}`;
    const res = await apiCall("get", endpoint, filterData, false);
    if (res?.status !== false) {
      const payload = res?.data?.data;
      if (payload) {
        setRecordDetails(payload.contentDetails || []);
        setContentTypeDetails(payload.contentTypeDetails || []);
        setContentTopicDetails(payload.contentTopicDetails || []);
      }
    } else {
      manageNetworkError(res);
    }
    setLoading(false);
  };

  useEffect(() => {
    getRecordDetails();
    // eslint-disable-next-line
  }, [resourceType, filterData]);

  const handleFilterField = (e) => {
    const { name, value } = e.target;
    if (name === "content_type") {
      if (!value) {
        setFilterData({});
        navigate(`/resources/`);
        return;
      }
      const selected = contentTypeDetails.find((ct) => String(ct?.id) === String(value));
      if (!selected) return;
      const slug = selected.value.toString().trim().toLowerCase();
      setFilterData({});
      navigate(`/resources/${slug}/`);
      return;
    }
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === 13 || e.which === 13) {
      getRecordDetails();
    }
  };

  const handleSelectTag = (tag) => {
    const selectedTag = contentTopicDetails.find((topic) => topic?.value === tag);
    if (selectedTag?.id) {
      const el = document.querySelector('[name="content_topics"]');
      if (el) el.value = selectedTag.id;
      setFilterData((prev) => ({ ...prev, content_topics: selectedTag.id }));
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    if (state?.tag && contentTopicDetails.length) {
      handleSelectTag(state.tag);
      localStorage.removeItem(Constants.RESOURCES_KEY_NAME);
    }
    // eslint-disable-next-line
  }, [state, contentTopicDetails]);

  const selectedContentTypeId =
    resourceType === "all"
      ? ""
      : contentTypeDetails.find((ct) => ct?.value?.toLowerCase() === resourceType)?.id || "";

  const dynamicTitle =
    resourceType === "all"
      ? pageName
      : `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)} | ${pageName}`;

  const seoData = {
    seo_title: dynamicTitle,
    seo_description:
      resourceType === "newsletter"
        ? "Weekly consulting industry newsletters and analysis."
        : "Consulting insights, articles and case studies supporting consulting delivery and execution.",
    canonical_url: canonicalUrl,
  };

  if (!loading && recordDetails.length === 0 && resourceType !== "all") {
    return (
      <Layout pageName={pageName} seoData={null} pageUrl={canonicalUrl}>
        <div style={{ minHeight: "60vh" }} />
      </Layout>
    );
  }

  return (
    <Layout pageName={pageName} seoData={seoData} pageUrl={canonicalUrl}>
      <Breadcrumbs title={pageName} text="Consulting resources and insights for boutique firm leaders" />

      {isNewsletterView && (
        <section className="sec-padding border-bottom">
          <div className="container">
            <NewsletterForm title="Stay tuned for next week's consulting industry update" />
          </div>
        </section>
      )}

      <section className="sec-padding border-bottom">
        <div className="container">

          {!isNewsletterView && (
            <div className="filter-row-parent">
              <div className="row filter-row w-lg-100">
                <div className="col-md-4">
                  <input
                    type="text"
                    name="search"
                    placeholder="Search"
                    className="form-control bordered-input"
                    onKeyPress={handleKeyPress}
                    onChange={handleFilterField}
                  />
                </div>
                <div className="col-md-4">
                  <select
                    name="content_type"
                    className="form-select bordered-input"
                    value={selectedContentTypeId}
                    onChange={handleFilterField}
                  >
                    <option value="">All Content Type</option>
                    {contentTypeDetails
                      .filter(
                        t =>
                          t?.value?.toLowerCase() !== "newsletter" &&
                          t?.value?.toLowerCase() !== "newsletters"
                      )
                      .map((contentType, key) => {
                        const label = contentType?.value?.toString().trim();
                        const formatted = label ? label.charAt(0).toUpperCase() + label.slice(1) : "";
                        return (
                          <option key={key} value={contentType?.id}>
                            {formatted}
                          </option>
                        );
                      })}
                  </select>
                </div>
                <div className="col-md-4">
                  <select
                    name="content_topics"
                    className="form-select bordered-input"
                    onChange={handleFilterField}
                  >
                    <option value="">All Content Topics</option>
                    {contentTopicDetails.map((contentTopic, key) => (
                      <option key={key} value={contentTopic?.id}>
                        {contentTopic?.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <ButtonPrimary icon="fi fi-rr-search" title="Search" onClick={getRecordDetails} />
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center">Loading...</div>
          ) : recordDetails.length > 0 ? (
            <div className="insights-grid">
              {recordDetails.map((recordInfo, key) => {
                const typeSlug = (recordInfo?.content_type_slug || (resourceType === "all" ? "articles" : resourceType))
                  .toString()
                  .trim()
                  .toLowerCase();
                const url = `/resources/${typeSlug}/${recordInfo?.slug}/`;
                return (
                  <InsightsCard
                    key={key}
                    title={recordInfo?.content_title}
                    text={recordInfo?.short_description}
                    date={recordInfo?.publish_date}
                    category={recordInfo?.content_type}
                    tags={recordInfo?.content_tags}
                    to={url}
                    link={url}
                    onTagClick={(e, tag) => handleSelectTag(tag)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center">No Record Found.</div>
          )}
        </div>
      </section>
    </Layout>
  );
}
